/**
 * Client for the reseller admin API (backend routers/reseller.py, /reseller/*). The reseller is logged in on our
 * own site with their normal account; the token is the dashboard's (tgp_token).
 */
import { brandConfig } from '../../config/brand';

/** The TXT record that proves a domain belongs to this shop (backend tenants/service.py verify_record). */
export interface DnsVerify { type: 'TXT'; name: string; host: string; value: string }

export interface ServerDomain { hostname: string; kind: 'app' | 'www'; status: 'pending' | 'active' | 'unverified' | 'error'; check?: { pointsHere?: boolean; verified?: boolean; tls?: boolean; tlsError?: string; ips?: string[] } | null; checkedAt?: string | null }
export interface ServerTenant {
  id: string;
  status: 'draft' | 'live' | 'suspended';
  config: Record<string, unknown>;
  secrets: Record<string, boolean>;
  wizard: { step: string; reached: number };
  /** Still open, shown as a to-do list. Going live needs only `required` (the reseller terms). */
  missing: string[];
  required?: string[];
  methods: string[];
  domains: ServerDomain[];
  dns: { type: 'A' | 'CNAME'; target: string; verify?: DnsVerify | null };
  liveAt?: string | null;
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public missing?: string[]) { super(message); }
}

const base = () => brandConfig.apiUrl;

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = (() => { try { return localStorage.getItem('tgp_token') || ''; } catch { return ''; } })();
  const res = await fetch(`${base()}${path}`, {
    method,
    headers: { ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const detail = typeof data.detail === 'string' ? data.detail : data.detail?.message;
    throw new ApiError(detail || `Request failed (${res.status})`, res.status, data.detail?.missing);
  }
  return res.json() as Promise<T>;
}

export const resellerApi = {
  tenant: () => call<ServerTenant>('GET', '/reseller/tenant'),
  saveTenant: (config?: Record<string, unknown>, wizard?: { step?: string; reached?: number }) => call<ServerTenant>('PUT', '/reseller/tenant', { config, wizard }),
  saveSecrets: (fields: Record<string, string>) => call<ServerTenant>('PUT', '/reseller/tenant/secrets', fields),
  goLive: () => call<ServerTenant>('POST', '/reseller/tenant/go-live'),
  goOffline: () => call<ServerTenant>('POST', '/reseller/tenant/offline'),
  checkDomains: () => call<ServerTenant>('POST', '/reseller/tenant/domains/check'),
  testMail: (to?: string) => call<{ sent: boolean; to: string }>('POST', '/reseller/tenant/mail/test', { to }),
  world: () => call<Record<string, unknown>>('GET', '/reseller/world'),
  addCustomer: (c: { name: string; email: string; site?: string }) => call<{ id: string; invited: boolean }>('POST', '/reseller/customers', c),
  customerStatus: (id: string, status: 'Active' | 'Suspended') => call('POST', `/reseller/customers/${id}/status`, { status }),
  deleteCustomer: (id: string) => call('DELETE', `/reseller/customers/${id}`),
  resetLink: (id: string) => call<{ sent: boolean }>('POST', `/reseller/customers/${id}/reset-link`),
  loginAs: (id: string) => call<{ url: string }>('POST', `/reseller/customers/${id}/login-as`),
  give: (id: string, product: string, qty: number) => call<{ order: string; status: string }>('POST', `/reseller/customers/${id}/credit`, { product, qty }),
  toggleCampaign: (id: string) => call<{ status: string }>('POST', `/reseller/campaigns/${id}/toggle`),
  approve: (orderId: string) => call<{ order: string; status: string }>('POST', `/reseller/orders/${orderId}/approve`),
  reject: (orderId: string, reason: string) => call('POST', `/reseller/orders/${orderId}/reject`, { reason }),
  retry: () => call<{ delivered: number }>('POST', '/reseller/orders/retry'),
  reply: (ticketId: string, text: string) => call('POST', `/reseller/tickets/${ticketId}/reply`, { text }),
  ticketStatus: (ticketId: string, status: 'Open' | 'Closed') => call('POST', `/reseller/tickets/${ticketId}/status`, { status }),
  proofUrl: (orderId: string) => `${base()}/reseller/orders/${orderId}/proof`,
};

/** Opens the proof file with the reseller's token (a plain link would have no Authorization header). */
export async function openProof(orderId: string): Promise<void> {
  const win = window.open('', '_blank');
  const token = localStorage.getItem('tgp_token') || '';
  const res = await fetch(resellerApi.proofUrl(orderId), { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) { win?.close(); throw new ApiError('The file could not be opened.', res.status); }
  const url = URL.createObjectURL(await res.blob());
  if (win) win.location.href = url; else window.location.href = url;
}

/** Whether this page talks to the real API: the logged-in admin, not the design previews or tests. */
export function isLiveAdmin(): boolean {
  if (typeof window === 'undefined') return false;
  if (/^\/(reseller-preview|whitelabel-preview)/.test(window.location.pathname)) return false;
  try { return !!localStorage.getItem('tgp_token'); } catch { return false; }
}
